import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageCircle, X, Send, Bot, User, FileText, Stethoscope, 
  Pill, Eye, Activity, ChevronDown, ChevronUp, Loader2,
  Clipboard, Download, Check, AlertCircle, Sparkles, Brain,
  Camera, Target, Scan, Waves, Grid3X3
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Renderizza markdown semplice
const renderMarkdown = (text) => {
  if (!text) return '';
  let html = text
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/═+/g, '<hr class="my-2 border-[#2A9FCB]/30">')
    .replace(/\n/g, '<br>');
  return html;
};

// Tipi di referto disponibili
const REPORT_TYPES = [
  { id: 'retinografia', name: 'Retinografia', icon: Camera, color: 'bg-blue-500' },
  { id: 'campo_visivo', name: 'Campo Visivo', icon: Target, color: 'bg-green-500' },
  { id: 'topografia', name: 'Topografia', icon: Grid3X3, color: 'bg-purple-500' },
  { id: 'oct', name: 'OCT', icon: Scan, color: 'bg-orange-500' },
  { id: 'oct_angio', name: 'OCT-Angio', icon: Waves, color: 'bg-red-500' },
  { id: 'fluorangiografia', name: 'FAG', icon: Eye, color: 'bg-pink-500' },
  { id: 'biometria', name: 'Biometria', icon: Activity, color: 'bg-cyan-500' },
  { id: 'pentacam', name: 'Pentacam', icon: Grid3X3, color: 'bg-indigo-500' },
  { id: 'ecografia_bulbare', name: 'Ecografia', icon: Waves, color: 'bg-teal-500' },
  { id: 'aberrometria', name: 'Aberrometria', icon: Sparkles, color: 'bg-amber-500' },
];

const MedicalAIAssistant = ({ patientId = null, patientName = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // chat, reports, diagnosis, therapy
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [includePatientData, setIncludePatientData] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  // Report generation state
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [reportFindings, setReportFindings] = useState('');
  const [reportContext, setReportContext] = useState('');
  const [reportEye, setReportEye] = useState('OU');
  const [generatedReport, setGeneratedReport] = useState(null);
  
  // Diagnosis state
  const [diagnosisComplaint, setDiagnosisComplaint] = useState('');
  const [generatedDiagnosis, setGeneratedDiagnosis] = useState(null);
  
  // Therapy state
  const [therapyDiagnosis, setTherapyDiagnosis] = useState('');
  const [therapySeverity, setTherapySeverity] = useState('moderata');
  const [generatedTherapy, setGeneratedTherapy] = useState(null);
  
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Messaggio iniziale
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `🩺 **Assistente AI Oftalmologico**\n\nBuongiorno, sono il tuo assistente medico AI specializzato in oftalmologia.\n\n**Posso aiutarti con:**\n• Consulenza su patologie oculari\n• Interpretazione esami diagnostici\n• Generazione referti professionali\n• Supporto diagnostico differenziale\n• Suggerimenti terapeutici evidence-based\n\n${patientName ? `📋 Paziente in esame: **${patientName}**` : 'Seleziona un paziente per analisi contestuale.'}\n\nCome posso assisterti?`
      }]);
    }
  }, [isOpen, patientName]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai-medical/chat`, {
        message: userMessage,
        session_id: sessionId,
        patient_id: patientId,
        include_patient_data: includePatientData && !!patientId
      }, { headers });

      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nella comunicazione con l\'AI');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '❌ Si è verificato un errore. Riprova.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    if (!selectedReportType) {
      toast.error('Seleziona un tipo di referto');
      return;
    }
    setLoading(true);
    setGeneratedReport(null);

    try {
      const res = await axios.post(`${API}/ai-medical/generate-report`, {
        report_type: selectedReportType,
        patient_id: patientId,
        findings: reportFindings || null,
        clinical_context: reportContext || null,
        eye: reportEye
      }, { headers });

      setGeneratedReport(res.data);
      toast.success('Referto generato con successo');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Errore nella generazione del referto');
    } finally {
      setLoading(false);
    }
  };

  const generateDiagnosis = async () => {
    if (!patientId) {
      toast.error('Seleziona un paziente per generare la diagnosi');
      return;
    }
    setLoading(true);
    setGeneratedDiagnosis(null);

    try {
      const res = await axios.post(`${API}/ai-medical/generate-diagnosis`, {
        patient_id: patientId,
        include_anamnesis: true,
        include_exams: true,
        include_visits: true,
        specific_complaint: diagnosisComplaint || null
      }, { headers });

      setGeneratedDiagnosis(res.data);
      toast.success('Diagnosi AI generata');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Errore nella generazione della diagnosi');
    } finally {
      setLoading(false);
    }
  };

  const generateTherapy = async () => {
    if (!therapyDiagnosis.trim()) {
      toast.error('Inserisci una diagnosi');
      return;
    }
    setLoading(true);
    setGeneratedTherapy(null);

    try {
      const res = await axios.post(`${API}/ai-medical/suggest-therapy`, {
        diagnosis: therapyDiagnosis,
        patient_id: patientId,
        severity: therapySeverity
      }, { headers });

      setGeneratedTherapy(res.data);
      toast.success('Suggerimento terapeutico generato');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Errore nella generazione del suggerimento');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiato negli appunti');
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#2A9FCB] to-[#1C729E] text-white rounded-full shadow-2xl hover:scale-110 transition-all duration-300 flex items-center justify-center z-[9999] group"
        data-testid="medical-ai-open-btn"
      >
        <Brain className="w-7 h-7 sm:w-8 sm:h-8 group-hover:rotate-12 transition-transform" />
        <span className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full animate-pulse" />
      </button>
    );
  }

  return (
    <div 
      className={`fixed z-[9999] bg-white shadow-2xl flex flex-col overflow-hidden ${
        isMobile 
          ? 'inset-0' 
          : 'bottom-6 right-6 w-[380px] rounded-2xl border border-gray-200'
      }`}
      style={{
        height: isMobile ? '100%' : 'min(520px, calc(100vh - 120px))',
        maxHeight: isMobile ? '100%' : 'calc(100vh - 120px)'
      }}
    >
      {/* Header - Compatto su mobile */}
      <div className={`bg-gradient-to-r from-[#2A9FCB] to-[#1C729E] text-white shrink-0 ${isMobile ? 'p-3' : 'p-4'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} bg-white/20 rounded-xl flex items-center justify-center`}>
              <Brain className={isMobile ? 'w-4 h-4' : 'w-6 h-6'} />
            </div>
            <div>
              <h3 className={`font-bold ${isMobile ? 'text-sm' : 'text-base'}`}>Assistente AI</h3>
              <p className="text-[10px] text-white/70">Supporto oftalmologico</p>
            </div>
          </div>
          <button 
            onClick={() => setIsOpen(false)} 
            className="hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className={isMobile ? 'w-5 h-5' : 'w-5 h-5'} />
          </button>
        </div>
        
        {/* Patient indicator - Solo se presente, compatto */}
        {patientName && (
          <div className="mt-2 bg-white/10 rounded-lg px-2 py-1.5 flex items-center gap-2">
            <User className="w-3 h-3" />
            <span className="text-xs truncate">Paziente: <strong>{patientName}</strong></span>
          </div>
        )}
        
        {/* Tabs - Compatti su mobile */}
        <div className={`flex gap-1 ${isMobile ? 'mt-2' : 'mt-3'}`}>
          {[
            { id: 'chat', label: isMobile ? 'Chat' : 'Chat', icon: MessageCircle },
            { id: 'reports', label: isMobile ? 'Referti' : 'Referti', icon: FileText },
            { id: 'diagnosis', label: isMobile ? 'Diagnosi' : 'Diagnosi', icon: Stethoscope },
            { id: 'therapy', label: isMobile ? 'Terapia' : 'Terapia', icon: Pill }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-1.5 px-1 rounded-lg text-[10px] sm:text-xs font-medium transition-all flex items-center justify-center gap-0.5 sm:gap-1 ${
                activeTab === tab.id 
                  ? 'bg-white text-[#2A9FCB]' 
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <tab.icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span className="hidden xs:inline sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content - Area scrollabile */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        
        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <>
            {/* Messages Area - Scrollabile */}
            <div className={`flex-1 overflow-y-auto space-y-3 ${isMobile ? 'p-3' : 'p-4'}`}>
              {messages.length === 0 && (
                <div className="text-center text-gray-400 text-sm py-8">
                  <Brain className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>Chiedimi informazioni cliniche,</p>
                  <p>diagnosi o terapie oftalmologiche</p>
                </div>
              )}
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${
                    msg.role === 'user' 
                      ? 'bg-[#2A9FCB] text-white rounded-br-sm' 
                      : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                  }`}>
                    <div 
                      className="text-xs sm:text-sm leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                    />
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl px-3 py-2 rounded-bl-sm">
                    <Loader2 className="w-4 h-4 animate-spin text-[#2A9FCB]" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Chat Input - Fisso in basso */}
            <div className={`border-t bg-gray-50 shrink-0 ${isMobile ? 'p-2' : 'p-3'}`}>
              {patientId && (
                <label className="flex items-center gap-2 text-[10px] text-gray-500 mb-1.5">
                  <input
                    type="checkbox"
                    checked={includePatientData}
                    onChange={(e) => setIncludePatientData(e.target.checked)}
                    className="rounded w-3 h-3"
                  />
                  Includi dati paziente
                </label>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Scrivi qui..."
                  className={`flex-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-[#2A9FCB]/30 focus:border-[#2A9FCB] ${isMobile ? 'text-sm' : 'text-sm'}`}
                />
                <button
                  onClick={sendMessage}
                  disabled={loading || !input.trim()}
                  className="px-3 py-2 bg-[#2A9FCB] text-white rounded-xl hover:bg-[#238bb3] disabled:opacity-50 transition-colors shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className={`flex-1 overflow-y-auto space-y-3 ${isMobile ? 'p-3' : 'p-4'}`}>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1.5 block">Tipo di Referto</label>
              <div className="grid grid-cols-2 gap-1.5">
                {REPORT_TYPES.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedReportType(type.id)}
                    className={`p-2 rounded-lg border-2 transition-all text-left ${
                      selectedReportType === type.id
                        ? 'border-[#2A9FCB] bg-[#2A9FCB]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <div className={`w-6 h-6 ${type.color} rounded-md flex items-center justify-center`}>
                        <type.icon className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-xs font-medium truncate">{type.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Occhio</label>
              <div className="flex gap-1.5">
                {['OD', 'OS', 'OU'].map(eye => (
                  <button
                    key={eye}
                    onClick={() => setReportEye(eye)}
                    className={`flex-1 py-1.5 rounded-lg border-2 text-xs font-medium transition-all ${
                      reportEye === eye
                        ? 'border-[#2A9FCB] bg-[#2A9FCB] text-white'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {eye}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Reperti (opzionale)</label>
              <textarea
                value={reportFindings}
                onChange={(e) => setReportFindings(e.target.value)}
                placeholder="Es: Drusen maculari..."
                className={`w-full p-2 border rounded-lg text-xs resize-none focus:ring-2 focus:ring-[#2A9FCB]/30 ${isMobile ? 'h-14' : 'h-16'}`}
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Contesto (opzionale)</label>
              <textarea
                value={reportContext}
                onChange={(e) => setReportContext(e.target.value)}
                placeholder="Es: Follow-up glaucoma..."
                className={`w-full p-2 border rounded-lg text-xs resize-none focus:ring-2 focus:ring-[#2A9FCB]/30 ${isMobile ? 'h-14' : 'h-16'}`}
              />
            </div>

            <button
              onClick={generateReport}
              disabled={loading || !selectedReportType}
              className="w-full py-2.5 bg-gradient-to-r from-[#2A9FCB] to-[#1C729E] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Genera Referto AI
            </button>

            {generatedReport && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-green-800 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Referto Generato
                  </span>
                  <button 
                    onClick={() => copyToClipboard(generatedReport.content)}
                    className="text-green-700 hover:text-green-900"
                  >
                    <Clipboard className="w-4 h-4" />
                  </button>
                </div>
                <div 
                  className={`text-xs text-gray-700 overflow-y-auto bg-white rounded-lg p-2 border ${isMobile ? 'max-h-32' : 'max-h-48'}`}
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(generatedReport.content) }}
                />
                <p className="text-[10px] text-amber-600 mt-1.5 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  BOZZA - Verificare prima dell'uso
                </p>
              </div>
            )}
          </div>
        )}

        {/* Diagnosis Tab */}
        {activeTab === 'diagnosis' && (
          <div className={`flex-1 overflow-y-auto space-y-3 ${isMobile ? 'p-3' : 'p-4'}`}>
            {!patientId ? (
              <div className="text-center py-6">
                <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                <p className="text-gray-600 text-sm">Seleziona un paziente per generare una diagnosi AI</p>
              </div>
            ) : (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                  <p className="text-xs text-blue-800">
                    L'AI analizzerà: <strong>anamnesi</strong>, <strong>visite</strong>, 
                    <strong> esami</strong> e <strong>cartella</strong> del paziente.
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">
                    Sintomo specifico (opzionale)
                  </label>
                  <textarea
                    value={diagnosisComplaint}
                    onChange={(e) => setDiagnosisComplaint(e.target.value)}
                    placeholder="Es: Calo visivo improvviso OD..."
                    className={`w-full p-2 border rounded-lg text-xs resize-none focus:ring-2 focus:ring-[#2A9FCB]/30 ${isMobile ? 'h-16' : 'h-20'}`}
                  />
                </div>

                <button
                  onClick={generateDiagnosis}
                  disabled={loading}
                  className="w-full py-2.5 bg-gradient-to-r from-[#2A9FCB] to-[#1C729E] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stethoscope className="w-4 h-4" />}
                  Genera Diagnosi AI
                </button>

                {generatedDiagnosis && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-green-800 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Analisi Diagnostica
                      </span>
                      <button 
                        onClick={() => copyToClipboard(generatedDiagnosis.content)}
                        className="text-green-700 hover:text-green-900"
                      >
                        <Clipboard className="w-4 h-4" />
                      </button>
                    </div>
                    <div 
                      className={`text-xs text-gray-700 overflow-y-auto bg-white rounded-lg p-2 border ${isMobile ? 'max-h-36' : 'max-h-52'}`}
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(generatedDiagnosis.content) }}
                    />
                    <p className="text-[10px] text-amber-600 mt-1.5 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Supporto AI - Diagnosi finale spetta al medico
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Therapy Tab */}
        {activeTab === 'therapy' && (
          <div className={`flex-1 overflow-y-auto space-y-3 ${isMobile ? 'p-3' : 'p-4'}`}>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Diagnosi</label>
              <textarea
                value={therapyDiagnosis}
                onChange={(e) => setTherapyDiagnosis(e.target.value)}
                placeholder="Es: Glaucoma primario ad angolo aperto..."
                className={`w-full p-2 border rounded-lg text-xs resize-none focus:ring-2 focus:ring-[#2A9FCB]/30 ${isMobile ? 'h-16' : 'h-20'}`}
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Gravità</label>
              <div className="flex gap-1.5">
                {[
                  { id: 'lieve', label: 'Lieve', color: 'bg-green-500' },
                  { id: 'moderata', label: 'Moderata', color: 'bg-yellow-500' },
                  { id: 'severa', label: 'Severa', color: 'bg-red-500' }
                ].map(sev => (
                  <button
                    key={sev.id}
                    onClick={() => setTherapySeverity(sev.id)}
                    className={`flex-1 py-1.5 rounded-lg border-2 text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                      therapySeverity === sev.id
                        ? 'border-[#2A9FCB] bg-[#2A9FCB]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 ${sev.color} rounded-full`} />
                    {sev.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={generateTherapy}
              disabled={loading || !therapyDiagnosis.trim()}
              className="w-full py-2.5 bg-gradient-to-r from-[#2A9FCB] to-[#1C729E] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pill className="w-4 h-4" />}
              Suggerisci Terapia AI
            </button>

            {generatedTherapy && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-green-800 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Piano Terapeutico
                  </span>
                  <button 
                    onClick={() => copyToClipboard(generatedTherapy.content)}
                    className="text-green-700 hover:text-green-900"
                  >
                    <Clipboard className="w-4 h-4" />
                  </button>
                </div>
                <div 
                  className={`text-xs text-gray-700 overflow-y-auto bg-white rounded-lg p-2 border ${isMobile ? 'max-h-36' : 'max-h-52'}`}
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(generatedTherapy.content) }}
                />
                <p className="text-[10px] text-amber-600 mt-1.5 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Suggerimento AI - Prescrizione finale spetta al medico
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicalAIAssistant;
